import { useUpdateRecommendation } from '@/api/recommendation/recommendation.hooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Recommendation } from '@repo/types/src/recommendation';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  RecommendationForm,
  RecommendationFormState,
} from './RecommendationForm';

type EditRecommendationProps = {
  item: Recommendation;
  refetch: () => void;
};

export const EditRecommendation = ({
  item,
  refetch,
}: EditRecommendationProps) => {
  const mutation = useUpdateRecommendation();

  const toFormState = (): RecommendationFormState => ({
    textFr: item.textFr,
    textEn: item.textEn,
    whyFr: item.whyFr,
    whyEn: item.whyEn,
    symptomTypes: item.symptoms.map((s) => s.symptom.type),
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<RecommendationFormState>(toFormState());

  const handleUpdate = () => {
    if (!form.textFr || !form.textEn || !form.whyFr || !form.whyEn) {
      toast.error('Tous les champs texte sont obligatoires');
      return;
    }
    if (form.symptomTypes.length === 0) {
      toast.error('Sélectionnez au moins un symptôme');
      return;
    }

    mutation.mutate(
      { id: item.id, payload: form },
      {
        onSuccess: () => {
          setOpen(false);
          refetch();
          toast.success('Recommandation mise à jour avec succès', {
            duration: 3000,
          });
        },
        onError: () => {
          toast.error('Erreur lors de la mise à jour', { duration: 3000 });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setForm(toFormState());
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Pencil size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-center">
            Modifier la recommandation
          </DialogTitle>
          <DialogDescription className="text-center">
            Modifiez les symptômes associés à cette recommandation
          </DialogDescription>
        </DialogHeader>

        <RecommendationForm state={form} onChange={setForm} />

        <DialogFooter className="flex gap-2">
          <DialogClose asChild>
            <Button variant="ghost">Annuler</Button>
          </DialogClose>
          <Button
            variant="default"
            onClick={handleUpdate}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Mise à jour...' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
